import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContentRow from '../components/ContentRow';
import { useIsMobile } from '../hooks/use-mobile';
import {
  getAllLists,
  createList,
  deleteList,
  ContentItem,
  ContentListMap,
} from '../utils/contentService';
import PlaylistPlayerModal from '../components/PlaylistPlayerModal';
import { Plus, Trash2, Play, X } from 'lucide-react';

const MinhasListas = () => {
  const isMobile = useIsMobile();
  const [listas, setListas] = useState<ContentListMap>({});
  const [mostrarModal, setMostrarModal] = useState(false);
  const [nomeNovaLista, setNomeNovaLista] = useState('');
  const [playlistAtiva, setPlaylistAtiva] = useState<ContentItem[] | null>(null);

  useEffect(() => {
    const carregarListas = () => {
      const todas = getAllLists();
      setListas(todas);
    };

    carregarListas();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'minhasListas') carregarListas();
    };

    const handleCustomEvent = () => carregarListas();

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('minhasListasUpdated', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('minhasListasUpdated', handleCustomEvent);
    };
  }, []);

  const handleCriarLista = () => {
    const nome = nomeNovaLista.trim();
    if (!nome) return;

    const criada = createList(nome);
    if (criada) {
      setListas(getAllLists());
      setMostrarModal(false);
      setNomeNovaLista('');
      window.dispatchEvent(new Event('minhasListasUpdated'));
    } else {
      alert('Já existe uma lista com esse nome.');
    }
  };

  const handleRemoverLista = (nome: string) => {
    if (confirm(`Tem certeza que deseja remover a lista "${nome}"?`)) {
      deleteList(nome);
      setListas(getAllLists());
      window.dispatchEvent(new Event('minhasListasUpdated'));
    }
  };

  const listasComItens = Object.entries(listas).filter(([, itens]) => itens.length > 0);

  return (
    <div
      className="bg-[#141414] bg-cover bg-center min-h-screen"
      style={{ backgroundImage: "url('/fundo_layout2.png')" }}
    >
      <Header />
      <main className="pt-[120px] sm:pt-[130px] md:pt-[140px] p-4 md:p-8 relative z-0">
        <div className="container mx-auto">
          {!isMobile && (
            <img
              src="/zeze-apontando.png"
              alt="Zezé apontando"
              className="fixed w-[200px] sm:w-[300px] md:w-[400px] hidden md:block left-0 top-[-30%] translate-y-[50%] ml-5 z-0"
            />
          )}

          <div className={`${isMobile ? 'pl-0' : 'pl-0 md:pl-[20%] lg:pl-[25%]'} sub_main relative`}>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-4xl md:text-3xl text-white font-bold">Minhas Listas</h1>
              <button
                onClick={() => setMostrarModal(true)}
                className="btn btn-play w-fit ml-auto px-3 py-2 text-sm rounded flex items-center gap-1 transition-transform transform hover:scale-105 hover:opacity-80"
              >
                <Plus size={16} />
                Nova Lista
              </button>
            </div>

            {listasComItens.length > 0 ? (
              listasComItens.map(([nomeLista, itens]) => (
                <div
                  key={nomeLista}
                  className="mb-12 p-4 bg-black/30 rounded-xl border border-white/10"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-2xl font-semibold text-white">{nomeLista}</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPlaylistAtiva(itens)}
                        className="btn btn-play px-3 py-2 text-sm rounded flex items-center gap-1 transition-transform transform hover:scale-105 hover:opacity-80"
                      >
                        <Play size={16} />
                        Reproduzir Todos
                      </button>
                      <button
                        onClick={() => handleRemoverLista(nomeLista)}
                        className="btn btn-play px-3 py-2 text-sm rounded flex items-center gap-1 transition-transform transform hover:scale-105 hover:opacity-80"
                      >
                        <Trash2 size={16} />
                        Remover
                      </button>
                    </div>
                  </div>
                  <ContentRow title="" items={itens} category={nomeLista} />
                </div>
              ))
            ) : (
              <div className="text-white text-center p-10 bg-black/30 rounded-lg">
                <h2 className="text-xl mb-4">Nenhuma lista com itens</h2>
                <p>Adicione conteúdos às suas listas para visualizá-las aqui.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {mostrarModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
          <div className="bg-white text-black rounded-lg p-6 w-[90%] max-w-md shadow-lg">
            <h2 className="text-xl font-bold mb-4">Criar Nova Lista</h2>
            <input
              type="text"
              placeholder="Digite um nome para a lista"
              className="w-full border px-4 py-2 mb-4 rounded outline-none focus:ring-2 ring-rustico-brown"
              value={nomeNovaLista}
              onChange={(e) => setNomeNovaLista(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setMostrarModal(false)}
                className="btn btn-play px-3 py-2 text-sm rounded flex items-center gap-1 transition-transform transform hover:scale-105 hover:opacity-80"
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                onClick={handleCriarLista}
                className="btn btn-play px-3 py-2 text-sm rounded flex items-center gap-1 transition-transform transform hover:scale-105 hover:opacity-80"
              >
                <Plus size={16} />
                Criar
              </button>
            </div>
          </div>
        </div>
      )}

      {playlistAtiva && (
        <PlaylistPlayerModal
          items={playlistAtiva}
          onClose={() => setPlaylistAtiva(null)}
        />
      )}
    </div>
  );
};

export default MinhasListas;
